import { z } from "zod";
import { USER_ROLES } from "./enums.js";
// ── Auth ──────────────────────────────────────────────
export const loginSchema = z.object({
    email: z.string().email("Invalid email"),
    password: z.string().min(1, "Password is required"),
    rememberMe: z.boolean().optional(),
});
export const passwordSchema = z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain an uppercase letter")
    .regex(/[a-z]/, "Password must contain a lowercase letter")
    .regex(/[0-9]/, "Password must contain a number");
// ── Users (admin) ─────────────────────────────────────
export const createUserSchema = z.object({
    email: z.string().email(),
    displayName: z.string().min(1).max(100),
    password: passwordSchema,
    role: z.enum(USER_ROLES).default("viewer"),
});
export const updateUserSchema = z.object({
    displayName: z.string().min(1).max(100).optional(),
    role: z.enum(USER_ROLES).optional(),
    isActive: z.boolean().optional(),
});
export const resetPasswordSchema = z.object({
    password: passwordSchema,
});
// ── Decoded trap packet (broadcast & API response shape) ──
export const trapEventSchema = z.object({
    index: z.number().int().min(0),
    type: z.number().int().min(0).max(255),
    typeLabel: z.string(),
    offsetSec: z.number().int(),
    at: z.string(), // ISO datetime
});
export const trapPacketSchema = z.object({
    id: z.number().int().optional(), // present once persisted
    deviceId: z.string().length(24),
    pid: z.number().int(),
    temperature: z.number().int(), // ×100
    temperatureC: z.number(), // derived for FE convenience
    humidity: z.number().int().min(0).max(100),
    batteryVoltageMv: z.number().int(),
    batteryVoltageV: z.number(),
    deviceTimestamp: z.string(), // ISO datetime
    eventCount: z.number().int(),
    events: z.array(trapEventSchema),
    rawHex: z.string(),
    receivedAt: z.string(), // ISO datetime
});
// ── Dashboard query ───────────────────────────────────
export const trapQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(100),
    deviceId: z.string().optional(),
    eventType: z.coerce.number().int().min(0).max(255).optional(),
    search: z.string().optional(),
    // Numeric range filters (all optional, all inclusive).
    pid: z.coerce.number().int().min(0).max(255).optional(),
    tempCMin: z.coerce.number().optional(),
    tempCMax: z.coerce.number().optional(),
    humidityMin: z.coerce.number().int().min(0).max(100).optional(),
    humidityMax: z.coerce.number().int().min(0).max(100).optional(),
    batteryVMin: z.coerce.number().optional(),
    batteryVMax: z.coerce.number().optional(),
    eventCountMin: z.coerce.number().int().min(0).max(255).optional(),
    eventCountMax: z.coerce.number().int().min(0).max(255).optional(),
});
// ── Device summary ────────────────────────────────────
export const deviceSchema = z.object({
    deviceId: z.string().length(24),
    label: z.string().nullable(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    lastTemperature: z.number().int().nullable(),
    lastHumidity: z.number().int().nullable(),
    lastBatteryVoltageMv: z.number().int().nullable(),
});
// ── Public user shape (no password hash) ──────────────
export const publicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    role: z.enum(USER_ROLES),
    isActive: z.boolean(),
    lastLoginAt: z.string().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=schemas.js.map